import React, { useState, useEffect, useCallback } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import '../CSS/Checkout.css';
import '../CSS/CardReader.css';

// Initialize Stripe with publishable key from environment variable
// Only load Stripe if the key is configured
const stripePublishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
const stripePromise = stripePublishableKey ? loadStripe(stripePublishableKey) : null;

// Supabase Edge Function URLs
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

// Check if Supabase is configured
const isSupabaseConfigured = SUPABASE_URL && SUPABASE_ANON_KEY;

// Helper function to call Supabase Edge Functions
const callEdgeFunction = async (functionName, body) => {
  if (!isSupabaseConfigured) {
    console.warn('Supabase not configured - skipping edge function call');
    // Return mock data for development
    return { order: { id: 'mock-' + Date.now() } };
  }

  const response = await fetch(
    `${SUPABASE_URL}/functions/v1/${functionName}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
      },
      body: JSON.stringify(body),
    }
  );
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Edge function call failed');
  }
  
  return response.json();
};

function CardReaderScreen({ cart, total, onBack, onSuccess }) {
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);

  const handleCardReaderPayment = useCallback(async () => {
    setProcessing(true);
    setError(null);
    
    try {
      // Step 1: Save order to database first
      const orderData = await callEdgeFunction('save-order', {
        items: cart.map(item => ({
          item_name: item.item_name,
          quantity: item.quantity,
          price: item.totalPrice,
          selectedSize: item.selectedSize,
          selectedAddOns: item.selectedAddOns,
        })),
        subtotal: total,
        tax: 0, // Add tax calculation if needed
        total: total,
        status: 'pending'
      });

      console.log('Order saved:', orderData.order);

      // Step 2: Create payment intent with Stripe via Edge Function
      const paymentData = await callEdgeFunction('create-payment-intent', {
        amount: total,
        order_id: orderData.order.id
      });

      console.log('Payment intent created:', paymentData.paymentIntentId);

      // Step 3: Update order with payment intent ID
      await callEdgeFunction('save-order', {
        ...orderData.order,
        payment_intent_id: paymentData.paymentIntentId,
        payment_status: 'processing'
      });

      // TODO: Integrate with Stripe Terminal/Card Reader here
      // For physical card reader integration:
      // const terminal = await stripe.terminal.create();
      // const result = await terminal.collectPaymentMethod(paymentData.clientSecret);
      // await terminal.processPayment(result.paymentIntent);

      // For now, simulate successful payment after delay
      // In production, replace this with actual card reader integration
      setTimeout(async () => {
        try {
          // Update order status to completed
          await callEdgeFunction('save-order', {
            ...orderData.order,
            payment_intent_id: paymentData.paymentIntentId,
            status: 'completed',
            payment_status: 'succeeded'
          });
          onSuccess();
        } catch (err) {
          console.error('Error updating order:', err);
          onSuccess(); // Still proceed as payment was successful
        }
      }, 3000);

    } catch (err) {
      console.error('Payment error:', err);
      setError(err.message || 'Payment failed. Please try again.');
      setProcessing(false);
    }
  }, [cart, total, onSuccess]);

  // Auto-start payment when screen opens
  useEffect(() => {
    handleCardReaderPayment();
  }, [handleCardReaderPayment]);

  return (
    <div className="card-reader-screen">
      <div className="card-reader-content">
        <div className="payment-amount">
          <h2>${total.toFixed(2)}</h2>
        </div>
        <div className="card-reader-instructions">
          <h3>Tap or Insert Card Now</h3>
          <p>Please present your card to complete the payment</p>
        </div>
        <div className="card-reader-icon">
          💳
        </div>
        {processing && (
          <div className="processing-indicator">
            <div className="loader"></div>
            <p>Processing payment...</p>
          </div>
        )}
        {error && (
          <div className="error-message">
            <p>{error}</p>
            <button onClick={handleCardReaderPayment} className="retry-btn">
              Try Again
            </button>
          </div>
        )}
        <button className="cancel-payment-btn" onClick={onBack}>
          Cancel Payment
        </button>
      </div>
    </div>
  );
}

function CheckoutForm({ cart, total, onBack, onSuccess }) {
  const [showCardReader, setShowCardReader] = useState(false);

  const handlePayButtonClick = () => {
    setShowCardReader(true);
  };

  const handleCardReaderBack = () => {
    setShowCardReader(false);
  };

  if (showCardReader) {
    return (
      <CardReaderScreen
        cart={cart}
        total={total}
        onBack={handleCardReaderBack}
        onSuccess={onSuccess}
      />
    );
  }

  return (
    <div className="checkout-container">
      <div className="checkout-content">
        <div className="checkout-header">
          <h2>Checkout</h2>
          <button className="back-button" onClick={onBack}>Back to Cart</button>
        </div>

        <div className="order-summary">
          <h3>Order Summary</h3>
          <div className="order-items">
            {cart.map((item, index) => (
              <div key={index} className="order-item">
                <span className="item-name">{item.item_name}</span>
                <span className="item-quantity">x{item.quantity}</span>
                <span className="item-price">${(item.totalPrice * item.quantity).toFixed(2)}</span>
              </div>
            ))}
          </div>
          <div className="order-total">
            <span>Total</span>
            <span>${total.toFixed(2)}</span>
          </div>
        </div>

        <div className="payment-section">
          <h3>Payment</h3>
          <p className="payment-instruction">Ready to process payment with card reader</p>
          <button
            onClick={handlePayButtonClick}
            className="pay-button"
          >
            Pay ${total.toFixed(2)}
          </button>
        </div>
      </div>
    </div>
  );
}

function Checkout({ cart, total, onBack, onSuccess }) {
  // Show warning if Stripe is not configured
  if (!stripePromise) {
    return (
      <div className="checkout-container">
        <div className="checkout-content">
          <div className="checkout-header">
            <h2>Checkout</h2>
            <button className="back-button" onClick={onBack}>Back to Cart</button>
          </div>
          <div className="error-message" style={{ padding: '20px', textAlign: 'center' }}>
            <p>Payment system is not configured.</p>
            <p style={{ fontSize: '0.9rem', color: '#666', marginTop: '10px' }}>
              Please add REACT_APP_STRIPE_PUBLISHABLE_KEY to your .env file.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Elements stripe={stripePromise}>
      <CheckoutForm
        cart={cart}
        total={total}
        onBack={onBack}
        onSuccess={onSuccess}
      />
    </Elements>
  );
}

export default Checkout;
